<template>
    <Head title="Login" />
    <form
        method="POST"
        class="flex min-h-screen items-center justify-center bg-surface-50 dark:bg-surface-950"
        @submit.prevent="onSubmitClick"
    >
        <div
            class="flex w-full max-w-xl flex-col items-center gap-2 rounded-3xl bg-surface-0 px-6 pb-6 pt-12 shadow-[0px_24px_48px_0px_rgba(0,0,0,0.04)] dark:bg-surface-900"
        >
            <div>
                <a :href="route('landing')">
                    <ApplicationLogo />
                </a>
            </div>

            <div v-if="status" class="text-sm font-medium text-green-600 dark:text-green-400">
                {{ status }}
            </div>

            <div class="flex w-full flex-col">
                <div class="flex flex-col">
                    <label for="email">Email</label>
                    <InputText
                        id="email"
                        type="text"
                        v-model="form.email"
                        placeholder="Enter your Email Address..."
                        class="dark:!bg-surface-900"
                        autocomplete="email"
                        autofocus
                        :disabled="isEmailPreFilled ? true : false"
                        @input="onEmailChange"
                    />
                    <InputError class="mt-1" :message="form.errors.email ?? '&nbsp;'" />
                </div>
                <div class="flex flex-col">
                    <label for="password">Password</label>
                    <InputText
                        id="password"
                        type="password"
                        v-model="form.password"
                        placeholder="Enter your Password..."
                        class="dark:!bg-surface-900"
                        autocomplete="password"
                        @input="onPasswordChange"
                    />
                    <InputError class="mt-1" :message="form.errors.password ?? '&nbsp;'" />
                </div>
            </div>
            <div class="flex w-full items-center justify-between">
                <div class="flex items-center">
                    <Checkbox v-model="form.remember" input-id="remember" name="remember" />
                    <label for="remember" class="ml-2 text-surface-700 dark:text-surface-300"> Remember me </label>
                </div>
                <a
                    v-if="canResetPassword"
                    :href="route('password.request')"
                    class="font-medium text-primary transition-all hover:text-primary-emphasis"
                >
                    Forgot your password?
                </a>
            </div>
            <div class="w-full pt-4">
                <Button
                    label="Sign In"
                    class="w-full"
                    type="submit"
                    :disabled="form.processing"
                    @click="onSubmitClick"
                />
            </div>
            <div class="mt-1 font-medium text-surface-700 dark:text-surface-300">
                Don't have an account?
                <a :href="route('register')" class="text-primary transition-all hover:text-primary-emphasis"
                    >Register instead!</a
                >
            </div>
        </div>
    </form>
</template>
<script setup lang="ts">
import { Head, useForm, usePage } from '@inertiajs/vue3'
import { useToast } from 'primevue/usetoast'
import { onMounted, ref } from 'vue'
import { isNull } from 'lodash'
import { useHead } from '@vueuse/head'
import { route } from 'ziggy-js'
import { schemaTagOptions } from '@/Composables/SchemaTagOptions'
import { FlashI } from '@/Types/types'
import ApplicationLogo from '@/Components/ApplicationLogo.vue'
import Checkbox from '@/Components/Checkbox.vue'
import InputError from '@/Components/InputError.vue'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import { isEmailValid } from '@/Composables/isValidEmail'

defineProps({
    canResetPassword: Boolean,
    status: {
        type: String,
        default: null,
    },
})

const isEmailPreFilled = ref(false)
const pageProps = usePage<{ flash: FlashI }>().props
const toast = useToast()
const form = useForm({
    email: '',
    password: '',
    remember: false,
})

const onSubmitClick = () => {
    form.transform((data) => ({
        ...data,
        remember: form.remember ? 'on' : '',
    })).post(route('login'), {
        onFinish: () => form.reset('password'),
    })
}

const onEmailChange = () => {
    form.errors.email = undefined
}

const onPasswordChange = () => {
    form.errors.password = undefined
}

useHead(
    schemaTagOptions({
        title: 'Login | Docu-Vault',
        description:
            'Securely log in to your Docu-Vault account to manage documents, track revisions, and access files anytime.',
        schemaType: 'WebPage',
        robots: 'noindex, noarchive',
    }),
)

onMounted(() => {
    const queryEmail =
        (usePage().props.email as string) ?? new URLSearchParams(window.location.search).get('email') ?? ''

    // Set email in the form if found in the URL
    if (queryEmail) {
        if (isEmailValid(queryEmail)) {
            form.email = queryEmail
            isEmailPreFilled.value = true
        }
    }

    if (!isNull(pageProps.flash.severity)) {
        toast.add({
            severity: pageProps.flash.severity,
            summary: pageProps.flash.summary,
            detail: pageProps.flash.detail,
            life: 5000,
        })
    }
})
</script>
